import {
  Alert,
  Backdrop,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  TextField,
} from "@mui/material";
import axios from "axios.js";
import usePageTitle from "app/pageTitle";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";
// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import { useAppLocation } from "agent_location_context";
import { useAwaitDialog } from "app/components/status_dialog";
import DataFormComponent from "app/components/data_forms/data_form";

function PSASampleCustomer() {
  const pageTitle = `Product Sampling - Capture Customer`;
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const { applocation, permissionStatus, updateAppLocation } = useAppLocation();
  const statusDialog = useAwaitDialog();
  const dataFormRef = useRef();

  const [pageLoaded, setPageLoaded] = useState(false);
  const [resultData, setResultData] = useState({});
  const [quantities, setQuantities] = useState({});
  const [selectedProducts, setSelectedProducts] = useState({});
  const [customerName, setCustomerName] = useState(null);
  const [customerPhone, setCustomerPhone] = useState(null);
  const [customerEmail, setCustomerEmail] = useState(null);

  useEffect(() => {
    updateAppLocation();
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`agents/psa/${ref}/sampling/init.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setResultData(result.data.data);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  const handleQuantityChange = (ref, value) => {
    setQuantities((prev) => ({
      ...prev,
      [ref]: value,
    }));
  };

  const toggleProduct = (ref) => {
    setSelectedProducts((prev) => ({
      ...prev,
      [ref]: !prev[ref],
    }));
  };

  async function submitSample() {
    if (applocation?.lat === null) {
      setSeverity("error");
      setErrMsg(
        "GPS not available. Make sure to enable location and grant permission..",
      );
      setOpen(true);
      return;
    }

    if (customerName === null) {
      setSeverity("error");
      setErrMsg("Customer name is required.");
      setOpen(true);
      return;
    }

    if (customerEmail === null && customerPhone === null) {
      setSeverity("error");
      setErrMsg("Customer phone or email is required.");
      setOpen(true);
      return;
    }

    const selected = resultData.products.filter(
      (p) => selectedProducts[p.reference],
    );

    if (selected.length === 0) {
      setSeverity("error");
      setErrMsg("Please select at least one product.");
      setOpen(true);
      return;
    }

    for (const product of selected) {
      const qty = quantities[product.reference];
      if (!qty || Number(qty) <= 0) {
        setSeverity("error");
        setErrMsg(`Enter quantity for ${product.title}`);
        setOpen(true);
        return;
      }
    }

    let formValues = null;
    if (resultData.data_form !== null) {
      const formResult = dataFormRef.current.submit();
      if (formResult.error) {
        setSeverity("error");
        setErrMsg(formResult.error);
        setOpen(true);
        return;
      }
      formValues = formResult.values;
    }

    setShowLoading(true);

    const json = {
      reference: ref,
      name: customerName,
      email_address: customerEmail,
      phone_number: customerPhone,
      lat: applocation?.lat,
      lng: applocation?.lng,
      products: resultData.products
        .filter((p) => selectedProducts[p.reference])
        .map((p) => ({
          reference: p.reference,
          quantity: Number(quantities[p.reference]),
        })),
      data_form: formValues,
    };

    await axios
      .post("agents/psa/sampling/register.json", json)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          await statusDialog.show(result.data.data.message, "success");
          if (resultData.is_instant) {
            navigate(`/activations/product-sampling/feedback/${result.data.data.reference}`);
          } else {
            navigate(`/activations/product-sampling/${ref}`);
          }
        } else {
          await statusDialog.show(result.data.data.message, "error", "Failed");
        }
      })
      .catch(async () => {
        const err = `Unable to complete request. ${
          navigator.onLine
            ? ""
            : "Make sure you have an active internet connection"
        }`;
        setShowLoading(false);
        await statusDialog.show(err, "error", "Failed");
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <MDBox mt={1} mb={5}>
        {pageLoaded && (
          <MDBox>
            <MDBox>
              <MDBox display="flex" justifyContent="space-between" mb={2}>
                <MDBox>
                  <Button
                    color="dark"
                    size="small"
                    startIcon={<Icon>west</Icon>}
                    onClick={() => {
                      if (window.history.length > 1) {
                        window.history.back();
                      } else {
                        navigate(`/activations/product-sampling/${ref}`);
                      }
                    }}
                  >
                    Back
                  </Button>
                </MDBox>
              </MDBox>
              <Card>
                <MDBox p={2}>
                  <MDBox align="center">
                    <MDTypography variant="caption" color="text">
                      Campaign
                    </MDTypography>
                    <MDTypography variant="h5">{resultData.title}</MDTypography>
                  </MDBox>
                  <MDBox>
                    <Divider />
                  </MDBox>

                  <MDBox mt={3} mb={3}>
                    <MDTypography variant="h6" mb={2}>
                      Customer Information
                    </MDTypography>
                    <MDBox>
                      <MDBox>
                        <MDInput
                          fullWidth
                          label="Name"
                          type="text"
                          onChange={(e) => setCustomerName(e.target.value)}
                        />
                      </MDBox>
                      <MDBox mt={3}>
                        <MDInput
                          fullWidth
                          label="Phone Number"
                          type="text"
                          onChange={(e) => setCustomerPhone(e.target.value)}
                        />
                      </MDBox>
                      <MDBox mt={3}>
                        <MDInput
                          fullWidth
                          label="Email Address (Optional)"
                          type="text"
                          onChange={(e) => setCustomerEmail(e.target.value)}
                        />
                      </MDBox>
                    </MDBox>
                  </MDBox>

                  <MDBox>
                    <Divider />
                  </MDBox>

                  <MDBox mt={3}>
                    <MDTypography variant="h6" mb={2}>
                      Products Sampled
                    </MDTypography>
                    {resultData.products.map((product) => (
                      <MDBox mb={3} key={product.reference}>
                        <ListItem>
                          <Checkbox
                            type="checkbox"
                            checked={!!selectedProducts[product.reference]}
                            onChange={() => toggleProduct(product.reference)}
                            style={{ marginRight: 10 }}
                          />

                          <ListItemText
                            primary={
                              <MDTypography fontSize={14}>
                                {product.title}
                              </MDTypography>
                            }
                          />

                          {selectedProducts[product.reference] && (
                            <MDInput
                              label="Sample Quantity"
                              type="decimal"
                              size="small"
                              defaultValue={product.sample_quantity}
                              value={quantities[product.reference] || ""}
                              onChange={(e) =>
                                handleQuantityChange(
                                  product.reference,
                                  e.target.value,
                                )
                              }
                              inputProps={{ min: 1 }}
                              style={{ width: 100 }}
                            />
                          )}
                        </ListItem>
                      </MDBox>
                    ))}
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
            {resultData.data_form && (
              <MDBox mt={3}>
                <DataFormComponent
                  ref={dataFormRef}
                  dataForm={resultData.data_form}
                />
              </MDBox>
            )}
            <MDBox mt={5} mb={1}>
              <Card>
                <MDBox p={3}>
                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={submitSample}
                    fullWidth
                  >
                    Save Customer
                  </MDButton>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PSASampleCustomer;
